import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const CarouselContainer = styled.div`
  width: 100%;
  max-width: 600px;
  height: 400px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  background: ${props => props.theme === 'dark' ? '#121212' : '#fff'};
  box-shadow: none;
  margin: 0 auto;
`;

const CarouselTrack = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.5s ease-in-out;
  transform: translateX(-${props => props.currentSlide * 100}%);
`;

const CarouselSlide = styled.div`
  min-width: 100%;
  height: 100%;
`;

const CarouselImage = styled.div`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: transform 0.5s ease;
  background-image: url(${props => props.src});
  
  &:hover {
    transform: scale(1.05);
  }
`;

const CarouselDots = styled.div`
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 10px;
  z-index: 10;
`;

const CarouselDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, ${props => props.active ? 0.9 : 0.5});
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: scale(${props => props.active ? 1.2 : 1});
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.8);
  }
`;

const Carousel = ({ images = [], autoPlayInterval = 5000, theme }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = images.length;
  
  useEffect(() => {
    if (autoPlayInterval <= 0 || images.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, autoPlayInterval);
    
    return () => clearInterval(interval);
  }, [totalSlides, autoPlayInterval, images.length]);
  
  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  if (images.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8 }}
    >
      <CarouselContainer theme={theme}>
        <CarouselTrack currentSlide={currentSlide}>
          {images.map((src, index) => (
            <CarouselSlide key={index}>
              <CarouselImage src={src} />
            </CarouselSlide>
          ))}
        </CarouselTrack>
        
        <CarouselDots>
          {Array.from({ length: totalSlides }).map((_, index) => (
            <CarouselDot
              key={index}
              active={currentSlide === index}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </CarouselDots>
      </CarouselContainer>
    </motion.div>
  );
};

export default Carousel;
